import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { LineChartIcon } from 'lucide-react';

import {
  DataTable,
  DataTablePageContent,
  DataTablePagination,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatDate } from '@/shared/helpers';
import { InsightDTO } from '@/shared/types';

type InsightListItem = Omit<InsightDTO, 'id'> & { id: string };

const columnHelper = createColumnHelper<InsightListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : undefined;
    },
  }),
  columnHelper.accessor('updatedAt', {
    header: 'Last updated',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : undefined;
    },
  }),
];

export function InsightList() {
  const { analyticsGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<InsightListItem>('/analytics/insights');

  const { state, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {},
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['insights'],
    queryFn: ({ signal }) => analyticsGateway.getInsights({ signal }),
  });

  const table = useDataTable({
    data: data?.items as InsightListItem[] | undefined,
    columns,
    isLoading,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <LineChartIcon />
        </HeaderIcon>
        <HeaderTitle>Insights</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
